import { t } from "@lingui/macro";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import BrandingWatermarkOutlinedIcon from "@mui/icons-material/BrandingWatermarkOutlined";
import CottageOutlinedIcon from "@mui/icons-material/CottageOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import PersonPinOutlinedIcon from "@mui/icons-material/PersonPinOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import VideoCameraFrontOutlinedIcon from "@mui/icons-material/VideoCameraFrontOutlined";
import TodayIcon from "@mui/icons-material/Today";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IntegrationInstructionsOutlinedIcon from "@mui/icons-material/IntegrationInstructionsOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import CastForEducationOutlinedIcon from "@mui/icons-material/CastForEducationOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { Tooltip } from "@mui/material";
import { Button, KeyboardShortcut } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useKeyboardShortcut from "use-keyboard-shortcut";
import { useSidebar } from "../sidebar-context";
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DeveloperModeOutlinedIcon from '@mui/icons-material/DeveloperModeOutlined';

type Props = { className?: string };

type SidebarItem = {
  path: string;
  name: string;
  shortcut?: string;
  icon: React.ReactNode;
};

type SidebarItemProps = SidebarItem & {
  onClick?: () => void;
  collapsed: boolean;
  showLabels: boolean;
};

type SidebarProps = {
  setOpen?: (open: boolean) => void;
};

const ActiveIndicator = ({ className }: Props) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className={cn(
      "size-1.5 animate-pulse rounded-full bg-info shadow-[0_0_12px] shadow-info",
      className,
    )}
  />
);

const SidebarItem = ({
  path,
  name,
  shortcut,
  icon,
  onClick,
  collapsed,
  showLabels,
}: SidebarItemProps) => {
  const isActive = useLocation().pathname === path;
  const isCollapsedView = !showLabels;

  const baseColors = isActive
    ? "bg-white/20 text-white hover:bg-white/20"
    : "text-slate-300 hover:bg-white/5 hover:text-white";

  const button = (
    <Button
  asChild
  size="lg"
  className={cn(
    "rounded-md transition-all duration-200",
    isCollapsedView ? "w-[80px] mx-auto" : "w-full",
    baseColors,
    isCollapsedView
      ? "h-auto px-2 py-2 justify-center"
      : "h-auto justify-start px-4 py-3",
  )}
  onClick={onClick}
>
  <Link
    to={path}
    className={cn(
      "w-full",
      isCollapsedView
        ? "flex flex-col items-center justify-center text-center"
        : "flex items-center",
    )}
  >
    <div
      className={cn(
        "flex items-center shrink-0 justify-center",
        isCollapsedView ? "mb-0.5 text-[15px]" : "mr-3 text-[16px]",
      )}
    >
      {icon}
    </div>

    {isCollapsedView ? (
      <span className="max-w-[80px] text-center text-[8.5px] leading-[11px] font-medium text-inherit">
        {name}
      </span>
    ) : (
      <>
        <span className="truncate text-[13px] font-medium">{name}</span>
        {!isActive && (
          <KeyboardShortcut className="ml-auto text-slate-400">
            {shortcut}
          </KeyboardShortcut>
        )}
        {isActive && <ActiveIndicator className="ml-auto" />}
      </>
    )}
  </Link>
</Button>
  );

  return isCollapsedView ? (
    <Tooltip title={name} placement="right" arrow>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

export const Sidebar = ({ setOpen }: SidebarProps) => {
  const navigate = useNavigate();
  const { collapsed, setCollapsed } = useSidebar();

  const [showLabels, setShowLabels] = useState(!collapsed);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (!collapsed) {
      timer = setTimeout(() => setShowLabels(true), 180);
    } else {
      setShowLabels(false);
    }
    return () => clearTimeout(timer);
  }, [collapsed]);

  // useKeyboardShortcut(["shift", "r"], () => {
  //   navigate("/resumes");
  //   setOpen?.(false);
  // });

  useKeyboardShortcut(["shift", "s"], () => {
    navigate("/profile");
    setOpen?.(false);
  });

  const sidebarItems: SidebarItem[] = [
    {
      path: "/",
      name: t`Home`,
      // shortcut: "⇧R",
      icon: <CottageOutlinedIcon fontSize="small" />,
    },
    {
      path: "/calendar",
      name: t`Calendar`,
      // shortcut: "⇧R",
      icon: <TodayIcon fontSize="small" />,
    },
    {
      path: "/lms",
      name: t`LMS`,
      // shortcut: "Shift+R",
      icon: <SchoolOutlinedIcon fontSize="small" />,
    },
    {
      path: "/coding-platform/dashboard",
      name: t`Coding Platform`,
      icon: <DeveloperModeOutlinedIcon fontSize="small" />,
    },
    {
      path: "/my-certificates",
      name: t`My Certificates`,
      icon: <WorkspacePremiumOutlinedIcon fontSize="small" />,
    },
    {
      path: "/jobs",
      name: t`Jobs`,
      // shortcut: "⇧R",
      icon: <BusinessCenterOutlinedIcon fontSize="small" />,
    },
    {
      path: "/assessments",
      name: t`Assessments`,
      // shortcut: "⇧R",
      icon: <AnalyticsOutlinedIcon fontSize="small" />,
    },
    {
      path: "/interviews",
      name: t`Interviews`,
      // shortcut: "⇧R",
      icon: <BrandingWatermarkOutlinedIcon fontSize="small" />,
    },
    {
      path: "/coding",
      name: t`Coding`,
      icon: <CodeOutlinedIcon fontSize="small" />,
    },
    {
      path: "/resume-builder",
      name: t`Resume Builder`,
      // shortcut: "⇧R",
      icon: <AutoFixHighOutlinedIcon fontSize="small" />,
    },
    {
      path: "/self-assessments",
      name: t`Self Assessments`,
      // shortcut: "⇧R",
      icon: <AssignmentOutlinedIcon fontSize="small" />,
    },
    {
      path: "/self-interviews",
      name: t`Self Interviews`,
      // shortcut: "⇧R",
      icon: <VideoCameraFrontOutlinedIcon fontSize="small" />,
    },
    {
      path: "/self-coding",
      name: t`Self Coding`,
      // shortcut: "⇧R",
      icon: <IntegrationInstructionsOutlinedIcon fontSize="small" />,
    },
    {
      path: "/webinar",
      name: t`Webinar`,
      // shortcut: "⇧R",
      icon: <CastForEducationOutlinedIcon fontSize="small" />,
    },
    {
      path: "/placement",
      name: t`Placement`,
      // shortcut: "⇧R",
      icon: <PersonPinOutlinedIcon fontSize="small" />,
    },
    {
      path: "/events",
      name: t`Events`,
      // shortcut: "⇧R",
      icon: <EventNoteOutlinedIcon fontSize="small" />,
    },
    // {
    //   path: "/resumes",
    //   name: t`Resumes`,
    //   // shortcut: "⇧R",
    //   icon: <ArticleOutlinedIcon fontSize="small" />,
    // },
  ];

  return (
    <div className="flex h-full flex-col">
      <div
  className={cn(
    "flex h-[40px] w-full shrink-0 items-center overflow-hidden border-b border-slate-400 bg-white py-0.5 font-medium transition-all duration-200 dark:bg-slate-900 dark:text-slate-100",
    collapsed ? "justify-center px-1.5" : "gap-2.5 px-3.5",
  )}
>
  <h1
    className={cn(
      "flex items-center whitespace-nowrap font-bold text-primary transition-all duration-200",
      collapsed ? "text-[15px]" : "text-[20px]",
    )}
  >
    <img
      src="/icon/logo-jobprep.png"
      alt="logo"
      className={cn(
        "shrink-0 transition-all duration-200",
        collapsed ? "size-5" : "size-6",
      )}
    />
    {t`obPrep`}
  </h1>
</div>

      <div className="flex flex-1 flex-col overflow-hidden bg-primary pl-2 pr-1 py-2">
        <div
          className={cn(
            "flex-1 overflow-x-hidden overflow-y-auto",
            !showLabels && "flex justify-center",
            "[&::-webkit-scrollbar]:w-[3px]",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:rounded-full",
            "[&::-webkit-scrollbar-thumb]:bg-slate-400/40",
            "hover:[&::-webkit-scrollbar-thumb]:bg-slate-400/60",
          )}
        >
          <div
            className={cn(
              "grid w-full gap-y-1",
              !showLabels && "place-items-center",
            )}
          >
            {sidebarItems.map((item) => (
              <SidebarItem
                {...item}
                key={item.path}
                collapsed={collapsed}
                showLabels={showLabels}
                onClick={() => setOpen?.(false)}
              />
            ))}
          </div>
        </div>

        <div
          className={cn(
            "mt-auto w-full shrink-0 border-t border-primary-accent pt-3",
            !showLabels ? "flex justify-end pr-3" : "flex justify-end pr-3",
          )}
        >
          <Tooltip
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            placement="right"
            arrow
          >
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-500 bg-primary text-slate-50 shadow-md transition-colors duration-150 hover:bg-primary-accent"
            >
              {collapsed ? (
                <ChevronRightIcon sx={{ fontSize: 15 }} />
              ) : (
                <ChevronLeftIcon sx={{ fontSize: 15 }} />
              )}
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};